import type { Component, JSX } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";

/*
 * The three screens the sheet shows when there is no company to show.
 *
 * All three are written for the inside of `ScrollableContent`, which is where
 * the sub-sheet mounts them: no page-level padding, no `<h1>` — the sheet header
 * already carries the title, and a second top-level heading inside the body
 * would give the sub-sheet two of them.
 *
 * The messages are deliberately **not** `company.publicPage.error.*`: those
 * strings say "questa pagina", which is a lie inside a sheet opened over an
 * offer. They live under `joblist.detailSheet.company.*` instead. The «Riprova»
 * label is the exception: its key already exists and reusing it costs nothing
 * (plan D12).
 *
 * Two messages, not one. The line that matters is **absence vs failure** — the
 * same line the «Riprova» button already draws. Saying "non siamo riusciti a
 * caricare" on a 404, on a query that never ran, or on a 200 carrying a null
 * company blames the product for a breakage that never happened, in front of a
 * reader who is often anonymous and for whom this sheet is the first real
 * content they meet.
 */

const StateFrame: Component<{
  icon: string;
  message: string;
  children?: JSX.Element;
}> = (props) => (
  <div class="mx-auto flex max-w-sm flex-col items-center gap-3 py-12 text-center">
    <i
      class={`ph ${props.icon} text-3xl text-muted-foreground`}
      aria-hidden="true"
    />
    <p class="text-muted-foreground text-sm">{props.message}</p>
    {props.children}
  </div>
);

/**
 * The request is in flight (EP1).
 *
 * The identity block and nothing else: no section outlines, no hint, and above
 * all no «Vedi azienda». EP7 is the reason — until the id is certain there must
 * be no moment in which the user can click through to a company that may not be
 * the one they are looking at.
 */
export const CompanyInfoSkeleton: Component = () => (
  <div class="flex w-full items-center gap-3" aria-hidden="true">
    <div class="size-11 shrink-0 animate-pulse rounded-md bg-muted" />
    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <div class="h-4 w-40 max-w-full animate-pulse rounded bg-muted" />
      <div class="h-3 w-56 max-w-full animate-pulse rounded bg-muted" />
    </div>
  </div>
);

/**
 * There is nothing to show and nothing to retry (EP3 + EP4).
 *
 * Covers the company that does not exist (404), the offer that never carried a
 * company id at all, and the 200 that answers with a null company: asking again
 * would produce the same answer, so no button is offered. None of the three is
 * a failure, so the message states an absence and stops there — no body line
 * explaining a breakage that did not happen. No sections, no «Vedi azienda» —
 * the offer behind the sheet stays the only identity available.
 */
export const CompanyInfoUnavailable: Component = () => {
  const { t } = useI18n();

  return (
    <StateFrame
      icon="ph-buildings"
      message={t("joblist.detailSheet.company.unavailable")}
    />
  );
};

/**
 * The request failed for a reason that may not repeat (EP2).
 *
 * The only screen of the three that may honestly claim a failure, and the only
 * one with a button: the query lives in the parent `DetailContent`, so closing
 * and reopening the sub-sheet refetches nothing — the cache holds the failure.
 * Without this the only way out was closing the whole offer.
 */
export const CompanyInfoLoadError: Component<{ onRetry: () => void }> = (
  props,
) => {
  const { t } = useI18n();

  return (
    <StateFrame
      icon="ph-cloud-warning"
      message={t("joblist.detailSheet.company.error")}
    >
      {/*
        Il `Button` condiviso porta nella sua base `active:scale-[0.97]`, e
        quella dichiarazione non sta dentro nessuna media query: a movimento
        ridotto il bottone si rimpicciolisce comunque alla pressione. Qui è
        l'unico controllo del modulo, e AC 18 chiede che questo foglio non
        muova nulla quando il sistema dice di non muovere nulla — quindi lo
        spegne questa istanza.

        Non `motion-reduce:transition-none`: toglierebbe anche colore e sfondo,
        che a movimento ridotto restano legittimi (la stessa scelta fatta per
        le icone social). E non sulla base di `buttonVariants`, che sarebbe la
        correzione giusta ma su ogni bottone del prodotto — un cambio con un
        raggio d'azione tutto suo, che non si porta dentro a questa spec.
      */}
      <Button
        variant="outline"
        size="sm"
        class="motion-reduce:active:scale-100"
        onClick={() => props.onRetry()}
      >
        {t("company.publicPage.error.retry")}
      </Button>
    </StateFrame>
  );
};
